import { Award, ChevronDown, ChevronLeft, ChevronRight, Clock, Loader2, MapPin, Quote, Search, Shield, Sparkles, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CarCard from "../components/CarCard";
import OffersShowcaseSection from "../components/OffersShowcaseSection";
import { SEO, buildFAQLD } from "../components/SEO";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useLanguage } from "../contexts/LanguageContext";
import odooApi from "../services/odooApi";

const ICON_MAP = {
  shield: Shield, clock: Clock, star: Star, award: Award, 'map-pin': MapPin,
};

export const HomePage = () => {
  const { t } = useLanguage();
  const [searchBrand, setSearchBrand] = useState("");
  const [searchCity, setSearchCity] = useState("");
  const [searchCategory, setSearchCategory] = useState("");
  const [featuredCars, setFeaturedCars] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [brands, setBrands] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [stats, setStats] = useState([]);
  const [features, setFeatures] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [siteConfig, setSiteConfig] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // New state for expanded sections
  const [showAllBrands, setShowAllBrands] = useState(false);
  const [showAllCategories, setShowAllCategories] = useState(false);

  // Homepage car lists
  const [exclusiveDeals, setExclusiveDeals] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [carsRes, catsRes, citiesRes, brandsRes, heroRes, statsRes, featRes, testRes, faqsRes, configRes] = await Promise.all([
          odooApi.getFeaturedCars(),
          odooApi.getCategories(),
          odooApi.getCities(),
          odooApi.getBrands(),
          odooApi.getHeroSlides(),
          odooApi.getStats(),
          odooApi.getFeatures(),
          odooApi.getTestimonials(),
          odooApi.getFaqs(),
          odooApi.getCmsConfig(),
        ]);
        setFeaturedCars(carsRes);
        setCategories(catsRes);
        setCities(citiesRes);
        setBrands(brandsRes);
        setHeroSlides(heroRes);
        setStats(statsRes);
        setFeatures(featRes);
        setTestimonials(testRes);
        setFaqs(faqsRes);
        setSiteConfig(configRes);

        // Derive homepage exclusive section from featured cars.
        if (carsRes.length > 0) {
          setExclusiveDeals(carsRes.filter(c => c.is_exclusive || c.pricePerDay > 1000).slice(0, 4));
        }
      } catch (err) {
        console.error("Failed to load homepage data:", err);
      } finally {
        setLoading(false);
      }
    };

    const fetchOffers = async () => {
      try {
        setOffers(await odooApi.getOffers());
      } catch (err) {
        console.error("Failed to load homepage offers:", err);
        setOffers([]);
      }
    };

    fetchData();
    fetchOffers();
  }, []);

  // Auto-rotate hero slides
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const activeSlide = heroSlides[currentSlide] || {
    title: t("home.experienceLuxury", "Experience Luxury On Every Journey"),
    subtitle: t(
      "home.heroFallbackSubtitle",
      "Discover the finest collection of luxury, sports, and premium vehicles for rent across the UAE.",
    ),
    badge_text: t("home.premiumCarRental", "Premium Car Rental UAE"),
    image_url: 'https://images.unsplash.com/photo-1634823929885-b12342dfc408?w=1920&h=1080&fit=crop',
    cta_text: t("btn.browseAllCars", "Browse All Cars"),
    cta_link: '/cars',
    cta_secondary_text: t("btn.contactUs", "Contact Us"),
    cta_secondary_link: '/contact',
  };

  // Default fallback stats
  const displayStats = stats.length > 0 ? stats : [
    { label: t("home.statsCarsAvailable", "Cars Available"), value: "1,200+" },
    { label: t("home.statsHappyCustomers", "Happy Customers"), value: "50,000+" },
    { label: t("home.statsPartnerSuppliers", "Partner Suppliers"), value: "150+" },
    { label: t("home.statsCitiesCovered", "Cities Covered"), value: "7" },
  ];

  // Default fallback features
  const displayFeatures = features.length > 0 ? features : [
    {
      icon: 'shield',
      title: t("home.featureInsuredTitle", "Fully Insured"),
      description: t(
        "home.featureInsuredDescription",
        "All vehicles come with comprehensive insurance coverage",
      ),
    },
    {
      icon: 'clock',
      title: t("home.featureSupportTitle", "24/7 Support"),
      description: t(
        "home.featureSupportDescription",
        "Round-the-clock customer support for your convenience",
      ),
    },
    {
      icon: 'star',
      title: t("home.featureFleetTitle", "Premium Fleet"),
      description: t(
        "home.featureFleetDescription",
        "Latest models from world-renowned luxury brands",
      ),
    },
    {
      icon: 'award',
      title: t("home.featurePricesTitle", "Best Prices"),
      description: t(
        "home.featurePricesDescription",
        "Competitive rates with no hidden fees guaranteed",
      ),
    },
  ];

  // Displayed brands (limited or all)
  const displayedBrands = showAllBrands ? brands : brands.slice(0, 8);
  const displayedCategories = showAllCategories ? categories : categories.slice(0, 6);
  const searchHref = (() => {
    const params = new URLSearchParams();
    if (searchBrand) params.set("brand", searchBrand);
    if (searchCity) params.set("city", searchCity);
    if (searchCategory) params.set("category", searchCategory);
    const query = params.toString();
    return query ? `/cars?${query}` : "/cars";
  })();

  const latestOffersSection = (
    <OffersShowcaseSection offers={offers} testId="latest-offers-section" />
  );

  return (
    <>
      <SEO
        title={siteConfig?.seo?.meta_title ? siteConfig.seo.meta_title.replace(/\s*\|\s*Velaris Royal.*$/i, '') : 'Luxury Car Hire in Dubai & UAE'}
        description={siteConfig?.seo?.meta_description || 'Rent luxury cars in Dubai, Abu Dhabi & UAE. Rolls Royce, Lamborghini, Ferrari, Bentley & more. Best prices, instant booking at Velaris Royal Car Rental.'}
        keywords={siteConfig?.seo?.meta_keywords || 'car rental dubai, luxury car rental uae, rent lamborghini dubai, ferrari rental, rolls royce hire uae, sports car rental dubai, suv rental, car hire abu dhabi, premium car rental'}
        structuredData={faqs.length > 0 ? buildFAQLD(faqs) : undefined}
      />

      {/* Hero Section */}
      <section className="relative min-h-[100svh] md:h-[90vh] md:min-h-[600px] bg-black overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={activeSlide.image_url}
            alt={activeSlide.title || "Luxury car rental in UAE - Velaris Royal"}
            className="w-full h-full object-cover opacity-60 transition-opacity duration-1000"
            key={currentSlide}
          />
          <div className="hero-overlay absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center pb-28 md:pb-24 pt-20 md:pt-0">
          <div className="max-w-3xl animate-fadeIn">
            <span className="inline-block bg-gold-gradient text-black px-3 py-1 text-[10px] sm:text-xs uppercase tracking-[0.2em] font-semibold mb-3 sm:mb-6">
              {activeSlide.badge_text}
            </span>
            <h1 className="font-[var(--font-display)] text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-3 sm:mb-6 leading-tight">
              {activeSlide.title}
            </h1>
            <p className="text-white/70 text-sm sm:text-xl mb-5 sm:mb-10 max-w-xl">
              {activeSlide.subtitle}
            </p>

            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 sm:p-6 border border-white/20" data-testid="search-box">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <label className="text-white/60 text-[10px] sm:text-xs uppercase tracking-wider mb-1 sm:mb-2 block">{t("search.brand", "Brand")}</label>
                  <Select value={searchBrand} onValueChange={setSearchBrand}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white h-9 sm:h-10 text-sm"><SelectValue placeholder={t("search.selectBrand", "Select Brand")} /></SelectTrigger>
                    <SelectContent>
                      {brands.map((brand) => (<SelectItem key={brand.slug} value={brand.slug}>{brand.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white/60 text-[10px] sm:text-xs uppercase tracking-wider mb-1 sm:mb-2 block">{t("search.city", "City")}</label>
                  <Select value={searchCity} onValueChange={setSearchCity}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white h-9 sm:h-10 text-sm"><SelectValue placeholder={t("search.selectCity", "Select City")} /></SelectTrigger>
                    <SelectContent>
                      {cities.map((city) => (<SelectItem key={city.slug} value={city.slug}>{city.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-white/60 text-[10px] sm:text-xs uppercase tracking-wider mb-1 sm:mb-2 block">{t("search.category", "Category")}</label>
                  <Select value={searchCategory} onValueChange={setSearchCategory}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white h-9 sm:h-10 text-sm"><SelectValue placeholder={t("search.allCategories", "All Categories")} /></SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (<SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Link to={searchHref} className="w-full">
                    <Button className="w-full btn-gold h-9 sm:h-10 text-black font-bold uppercase tracking-wider text-xs sm:text-sm" data-testid="search-btn">
                      <Search className="w-4 h-4 mr-1 sm:mr-2" />{t("btn.search", "Search")}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Slide navigation */}
          {heroSlides.length > 1 && (
            <div className="absolute bottom-20 md:bottom-32 right-4 sm:right-8 flex items-center gap-2 sm:gap-3">
              <button onClick={() => setCurrentSlide((p) => (p - 1 + heroSlides.length) % heroSlides.length)} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#D4AF37] transition-colors">
                <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <div className="flex gap-1.5 sm:gap-2">
                {heroSlides.map((_, i) => (
                  <button key={i} onClick={() => setCurrentSlide(i)} className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${i === currentSlide ? 'bg-[#D4AF37] w-4 sm:w-6' : 'bg-white/40'}`} />
                ))}
              </div>
              <button onClick={() => setCurrentSlide((p) => (p + 1) % heroSlides.length)} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-[#D4AF37] transition-colors">
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-md border-t border-[#D4AF37]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-6">
            <div className="grid grid-cols-4 gap-2 sm:gap-8">
              {displayStats.map((stat, index) => (
                <div key={index} className="text-center" data-testid={`stat-${index}`}>
                  <p className="text-lg sm:text-3xl font-bold text-gold-gradient">{stat.value}</p>
                  <p className="text-white/60 text-[10px] sm:text-sm mt-0.5 sm:mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Brands Section - Horizontal Scroll/Grid with Show More */}
      <section className="py-8 md:py-16 bg-black" data-testid="brands-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <span className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold">{t("sections.premiumPartners", "Premium Partners")}</span>
              <h2 className="font-[var(--font-display)] text-xl sm:text-3xl font-bold text-white mt-1">
                {t("sections.brands", "Explore by Brand")}
              </h2>
            </div>
            {brands.length > 8 && (
              <button
                onClick={() => setShowAllBrands(!showAllBrands)}
                className="flex items-center gap-1 text-[#D4AF37] hover:text-[#FFD700] text-sm font-medium transition-colors"
                data-testid="show-more-brands"
              >
                {showAllBrands ? t("btn.showLess", "Show Less") : t("btn.showMore", "Show More")}
                <ChevronDown className={`w-4 h-4 transition-transform ${showAllBrands ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
          
          {/* Horizontal scroll on mobile, grid on desktop */}
          <div className="overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0">
            <div className={`flex md:grid gap-3 ${showAllBrands ? 'md:grid-cols-8' : 'md:grid-cols-8'}`} style={{ minWidth: 'max-content' }}>
              {displayedBrands.map((brand) => (
                <Link key={brand.slug} to={`/brand/${brand.slug}`} className="group flex flex-col items-center justify-center p-3 sm:p-4 border border-[#D4AF37]/20 rounded-lg hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all min-w-[80px] md:min-w-0" data-testid={`brand-${brand.slug}`}>
                  <div className="w-15 h-15 sm:w-18 sm:h-18 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-2 group-hover:bg-[#D4AF37]/20 transition-colors overflow-hidden">
                    {brand.logo ? (
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        className="w-9 h-9 sm:w-12 sm:h-12 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <span className={`text-[#D4AF37] font-bold text-sm ${brand.logo ? 'hidden' : 'flex'} items-center justify-center`}>{brand.logoCode}</span>
                  </div>
                  <span className="text-white/80 text-xs text-center group-hover:text-[#D4AF37] transition-colors">{brand.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section with Show More */}
      <section className="py-12 md:py-24 bg-white dark:bg-gray-950" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold">{t("sections.browseByType", "Browse by Type")}</span>
              <h2 className="font-[var(--font-display)] text-2xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-2">
                {t("sections.categories", "Explore Our Categories")}
              </h2>
            </div>
            {categories.length > 6 && (
              <button
                onClick={() => setShowAllCategories(!showAllCategories)}
                className="flex items-center gap-1 text-[#D4AF37] hover:text-[#C5A028] text-sm font-medium transition-colors"
                data-testid="show-more-categories"
              >
                {showAllCategories ? t("btn.showLess", "Show Less") : t("btn.showMore", "Show More")}
                <ChevronDown className={`w-4 h-4 transition-transform ${showAllCategories ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayedCategories.map((category, index) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="category-card relative h-64 rounded-lg overflow-hidden group bg-gradient-to-br from-neutral-950 via-neutral-900 to-[#6b5214]"
                  data-testid={`category-${category.slug}`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {category.image ? (
                    <img src={category.image} alt={category.name} className="category-image absolute inset-0 w-full h-full object-cover" />
                  ) : null}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                    <h3 className="font-[var(--font-display)] text-2xl font-semibold text-white mb-1">{category.name}</h3>
                    <p className="text-white/60 text-sm">{category.carCount} {t("common.vehicles", "vehicles")}</p>
                  </div>
                  <div className="absolute top-4 right-4 w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20">
                    <ChevronRight className="w-5 h-5 text-black" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {latestOffersSection}

      {/* Exclusive Deals Section */}
      {(exclusiveDeals.length > 0 || featuredCars.length > 0) && (
        <section className="relative overflow-hidden py-12 md:py-24 bg-gray-50 dark:bg-gray-900" data-testid="exclusive-deals-section">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(124,58,237,0.18),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(212,175,55,0.18),_transparent_26%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-10 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-purple-500 text-xs uppercase tracking-[0.2em] font-semibold">{t("sections.premiumSelection", "Premium Selection")}</span>
                  <h2 className="font-[var(--font-display)] text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                    {t("sections.exclusiveDeals", "Exclusive Deals")}
                  </h2>
                </div>
              </div>
              <Link to="/cars?filter=exclusive" className="flex items-center gap-2 text-purple-500 hover:text-purple-600 transition-colors font-medium" data-testid="view-exclusive">
                {t("btn.viewExclusive", "View Exclusive")}<ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {(exclusiveDeals.length > 0 ? exclusiveDeals : featuredCars.slice(0, 4)).map((car) => (
                <CarCard key={car.id} car={car} badge="exclusive" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Cars Section */}
      <section className="relative overflow-hidden py-12 md:py-24 bg-white dark:bg-gray-950" data-testid="featured-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(212,175,55,0.16),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(17,24,39,0.08),_transparent_26%)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(212,175,55,0.14),_transparent_20%),radial-gradient(circle_at_bottom_right,_rgba(255,255,255,0.06),_transparent_24%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12 gap-4">
            <div>
              <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-semibold">{t("sections.handPicked", "Hand-picked Selection")}</span>
              <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-2">
                {t("sections.featured", "Featured Vehicles")}
              </h2>
            </div>
            <Link to="/cars" className="flex items-center gap-2 text-[#D4AF37] hover:text-[#C5A028] transition-colors font-medium" data-testid="view-all-cars">
              {t("btn.viewAllCars", "View All Cars")}<ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" /></div>
          ) : featuredCars.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredCars.map((car) => (<CarCard key={car.id} car={car} />))}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400"><p className="text-lg">{t("messages.featuredComingSoon", "Featured vehicles coming soon")}</p></div>
          )}
        </div>
      </section>

      {/* Cities Section */}
      <section className="py-12 md:py-24 bg-gray-50 dark:bg-gray-900" data-testid="cities-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold">{t("sections.availableLocations", "Available Locations")}</span>
            <h2 className="font-[var(--font-display)] text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mt-2 sm:mt-4">
              {t("sections.cities", "Rent Cars in UAE Cities")}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {cities.map((city) => (
              <Link key={city.slug} to={`/city/${city.slug}`} className="group relative bg-white dark:bg-gray-800 p-4 sm:p-8 rounded-lg hover:shadow-xl transition-all border border-transparent dark:border-gray-700 hover:border-[#D4AF37]" data-testid={`city-${city.slug}`}>
                <div className="flex items-center justify-between mb-2 sm:mb-4">
                  <h3 className="font-[var(--font-display)] text-base sm:text-2xl font-semibold text-gray-900 dark:text-white group-hover:text-[#D4AF37] transition-colors">{city.name}</h3>
                  <ChevronRight className="w-5 h-5 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-xs sm:text-base">{city.carCount} {t("common.carsAvailable", "cars available")}</p>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left rounded-b-lg" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features / Why Choose Us Section - CMS driven */}
      <section className="py-12 md:py-24 bg-white dark:bg-gray-950" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold">{t("sections.whyChooseUs", "Why Choose Us")}</span>
            <h2 className="font-[var(--font-display)] text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mt-2 sm:mt-4">
              {t("sections.velarisDifference", "The Velaris Difference")}
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {displayFeatures.map((feature, index) => {
              const Icon = ICON_MAP[feature.icon] || Shield;
              return (
                <div key={index} className="bg-gray-50 dark:bg-gray-900 p-4 sm:p-8 rounded-lg border-l-4 border-transparent hover:border-[#D4AF37] hover:shadow-lg transition-all" data-testid={`feature-${index}`}>
                  <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gold-gradient rounded-lg flex items-center justify-center mb-3 sm:mb-6">
                    <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-black" />
                  </div>
                  <h3 className="font-[var(--font-display)] text-sm sm:text-xl font-semibold text-gray-900 dark:text-white mb-1 sm:mb-3">{feature.title}</h3>
                  <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-xs sm:text-base">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section - CMS driven */}
      {testimonials.length > 0 && (
        <section className="py-12 md:py-24 bg-gray-50 dark:bg-gray-900" data-testid="testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-16">
              <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold">{t("sections.testimonials", "Testimonials")}</span>
              <h2 className="font-[var(--font-display)] text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mt-2 sm:mt-4">
                {t("sections.whatClientsSay", "What Our Clients Say")}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-transparent hover:border-[#D4AF37]/30 transition-all" data-testid={`testimonial-${i}`}>
                  <Quote className="w-8 h-8 text-[#D4AF37]/30 mb-4" />
                  <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">{t.review}</p>
                  <div className="flex items-center gap-4 border-t dark:border-gray-700 pt-4">
                    {t.photo_url ? (
                      <img src={t.photo_url} alt={t.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center"><span className="text-[#D4AF37] font-bold">{t.name?.substring(0, 2)}</span></div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{t.name}</p>
                      <p className="text-sm text-gray-500">{t.title}{t.country ? ` - ${t.country}` : ''}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-1">
                      <Star className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{t.rating}</span>
                    </div>
                  </div>
                  {t.car_rented && <p className="text-xs text-[#D4AF37] mt-3">Rented: {t.car_rented}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQs Section - CMS driven */}
      {faqs.length > 0 && (
        <section className="py-12 md:py-24 bg-white dark:bg-gray-950" data-testid="faqs-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-16">
              <span className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold">{t("sections.faq", "FAQ")}</span>
              <h2 className="font-[var(--font-display)] text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mt-2 sm:mt-4">
                {t("sections.faqTitle", "Frequently Asked Questions")}
              </h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <FaqAccordionItem key={i} question={faq.question} answer={faq.answer} index={i} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/faq" className="text-[#D4AF37] hover:text-[#C5A028] font-medium inline-flex items-center gap-2 transition-colors">
                {t("btn.viewAllFaqs", "View All FAQs")} <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-black relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=1920&h=600&fit=crop" alt="Luxury car rental fleet in Dubai UAE" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-[var(--font-display)] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t("cta.title", "Ready to Experience Luxury?")}
          </h2>
          <p className="text-white/70 text-lg mb-10 max-w-2xl mx-auto">{t("cta.subtitle", "Browse our premium collection and find your perfect ride. Book now and enjoy the royal treatment.")}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={activeSlide.cta_link || "/cars"} className="btn-gold px-10 py-4 text-black font-bold uppercase tracking-wider rounded-sm inline-flex items-center justify-center" data-testid="cta-browse">
              {activeSlide.cta_text || t("btn.browseAllCars", "Browse All Cars")}
            </Link>
            <Link to={activeSlide.cta_secondary_link || "/contact"} className="bg-transparent border-2 border-[#D4AF37] text-[#D4AF37] px-10 py-4 font-bold uppercase tracking-wider rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors inline-flex items-center justify-center" data-testid="cta-contact">
              {activeSlide.cta_secondary_text || t("btn.contactUs", "Contact Us")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;

function FaqAccordionItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-testid={`faq-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
        <span className="font-semibold text-gray-900 dark:text-white pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-[#D4AF37] transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-0 bg-white dark:bg-gray-800">
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}
